import Box from 'common/components/box/Box';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import styles from './RemindersScreen.module.scss';
import ReminderScreen from 'screens/vehicle/components/carReminderTab/components/ReminderScreen';
import Popover from 'common/components/popover/Popover';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import AddIcon from 'assets/svg/desktop_add_new.svg';
import { RemindersSearchCriteria } from 'api/reminders/models/RemindersSearchCriteria';
import { DATE_FORMAT_DEFAUT, DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import moment from 'moment';
import ReminderFiltersScreen, { Filters } from './components/ReminderFiltersScreen';
import FiltersIcon from 'assets/svg/desktop_filtres.svg';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import FilterBadgesOverflow, { FilterBadgeItem } from 'common/components/filterBadgesOverflow/FilterBadgesOverflow';
import { CompactPageLayout } from 'common/components/compactPageLayout';
import compactPageLayoutStyles from 'common/components/compactPageLayout/CompactPageLayout.module.scss';
import ExportListingButton from 'common/components/exportListingButton/ExportListingButton';
import ListRemindersScreen from './ListRemindersScreen';
import { ReminderListItemDto } from 'api/reminders/models/ReminderListItemDto';
import InfoCard from 'common/components/infoCard/InfoCard';
import colors from 'styles/export/colors.module.scss';
import RemindersService from 'api/reminders/RemindersService';
import { RemindersCountersDto } from 'api/reminders/models/RemindersCountersDto';
import Logger from 'common/services/Logger';
import AlertIcon from 'assets/svg/i_alertas_white.svg';
import ThresholdModal from 'common/components/thresholdModal/ThresholdModal';
import { FaPencilAlt } from 'react-icons/fa';

type RouteProps = {
    itemId: string;
    type: 'new' | 'edit' | 'details';
};

const THRESHOLD_CACHE_KEY = 'REMINDERS_THRESHOLDS';

type ThresholdKey = 'days' | 'km' | 'hours';
type Thresholds = Record<ThresholdKey, number>;
const DEFAULT_THRESHOLDS: Thresholds = { days: 30, km: 1000, hours: 50 };

// Load thresholds from localStorage (older caches only have days/km)
const loadThresholds = (): Thresholds => {
    try {
        const cached = localStorage.getItem(THRESHOLD_CACHE_KEY);
        if (cached) return { ...DEFAULT_THRESHOLDS, ...JSON.parse(cached) };
    } catch { /* ignore */ }
    return DEFAULT_THRESHOLDS;
};

type AlertRangeBadge = {
    key: string;
    label: string;
    presetsPrefix: string;
    preset?: string;
    from?: number;
    to?: number;
    unit: string;
    onRemove: () => void;
};

const RemindersScreen = () => {
    const { type } = useParams<RouteProps>();

    const FILTERS_CACHE_KEY = 'REMINDERSFILTER';
    const isFirstLoad = useRef<boolean>(true);
    const history = useHistory();
    const { t } = useTranslation();

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasRemindersWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_REMINDER_WRITE']);
    const exportListingRef = useRef<{ exportExcel: () => Promise<void> } | null>(null);

    const [filters, setFilters] = useState<Filters>({});
    const [filtersTotal, setFiltersTotal] = useState(0);
    const [counters, setCounters] = useState<RemindersCountersDto | null>(null);

    const [thresholds, setThresholds] = useState<Thresholds>(loadThresholds);
    const [editingThreshold, setEditingThreshold] = useState<ThresholdKey | null>(null);

    const [criteria, setCriteria] = useState<RemindersSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'r.created_date',
        orderBy: 'desc',
        vehiclesInactive: true,
    });

    const updateTotalFilters = (f: Filters) => {
        let total = 0;
        if (f.vehicleId && f.vehicleId.length > 0) total++;
        if (f.categoryId && f.categoryId.length > 0) total++;
        if (f.statuses && f.statuses.length > 0) total++;
        if (f.criteriaType) total++;
        if (f.reminderDateFrom != null || f.reminderDateTo != null) total++;
        if (f.alertDateFrom != null || f.alertDateTo != null) total++;
        if (f.alertKmFrom != null || f.alertKmTo != null) total++;
        if (f.alertHourFrom != null || f.alertHourTo != null) total++;
        if (f.segmentId) total++;
        if (f.localIds?.length) total++;
        setFiltersTotal(total);
    };

    const loadFilters = () => {
        const cachedFilters = localStorage.getItem(FILTERS_CACHE_KEY);
        if (cachedFilters != null) {
            onChangeFilters(JSON.parse(cachedFilters), true);
        } else {
            onChangeFilters({}, true);
        }
    };

    const onChangeFilters = (_filters: Filters, isOnLoad?: boolean) => {
        updateTotalFilters(_filters);
        setFilters({ ..._filters });
        localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(_filters));

        setCriteria(prev => ({
            ...prev,
            vehicleId: _filters.vehicleId,
            categoryId: _filters.categoryId,
            statuses: _filters.statuses,
            criteriaType: _filters.criteriaType,
            reminderDateFrom: _filters.reminderDateFrom,
            reminderDateTo: _filters.reminderDateTo,
            alertDateFrom: _filters.alertDateFrom,
            alertDateTo: _filters.alertDateTo,
            alertKmFrom: _filters.alertKmFrom,
            alertKmTo: _filters.alertKmTo,
            alertHourFrom: _filters.alertHourFrom,
            alertHourTo: _filters.alertHourTo,
            localIds: _filters.localIds,
            localOperator: _filters.localOperator,
            segmentId: _filters.segmentId,
            orderBy: _filters.orderBy || prev.orderBy || 'desc',
            orderColumn: _filters.orderColumn || prev.orderColumn || 'r.created_date',
            page: (isOnLoad && _filters.page) ? _filters.page : 1,
        }));

        isFirstLoad.current = false;
    };

    const loadCounters = useCallback(async () => {
        try {
            const countersCriteria: RemindersSearchCriteria = {
                ...criteria,
                alertInDays: thresholds.days,
                alertInKm: thresholds.km,
                alertInHours: thresholds.hours,
            };
            const data = await RemindersService.getCounters(countersCriteria);
            setCounters(data);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't load reminders counters`, error);
        }
    }, [criteria, thresholds]);

    useEffect(() => {
        if (!isFirstLoad.current) {
            loadCounters();
        }
    }, [loadCounters]);

    const applyThreshold = (key: ThresholdKey, value: number) => {
        const next = { ...thresholds, [key]: value };
        setThresholds(next);
        localStorage.setItem(THRESHOLD_CACHE_KEY, JSON.stringify(next));
        setEditingThreshold(null);
    };

    const thresholdFields: Record<ThresholdKey, { label: string; unit: string }> = {
        days: { label: t('reminders.counters.edit_days_threshold') as string, unit: t('common.days') as string },
        km: { label: t('reminders.counters.edit_km_threshold') as string, unit: 'km' },
        hours: { label: t('reminders.counters.edit_hours_threshold') as string, unit: t('common.hours_symbol') as string },
    };

    const navigateTo = (typeUrl?: string, id?: string) => {
        if (typeUrl) {
            history.push(typeUrl === 'new' ? '/reminders/new' : `/reminders/${typeUrl}/${id}`);
        } else {
            history.push('/reminders');
        }
    };

    useEffect(() => {
        loadFilters();
    }, []);

    const onRowClick = (item: ReminderListItemDto, event: any) => {
        event.preventDefault();
        event.stopPropagation();

        if (item.vehicleMaintenanceId && item.vehicleId) {
            const url = `/vehicles/details/${item.vehicleId}/maintenance/details/${item.vehicleMaintenanceId}`;
            history.push(url);
            return;
        }
        if (item.useContractId && item.vehicleId) {
            const url = `/vehicles/details/${item.vehicleId}/contract/details/use/details/${item.useContractId}`;
            history.push(url);
            return;
        }
        if (item.maintenanceContractId && item.vehicleId) {
            const url = `/vehicles/details/${item.vehicleId}/contract/details/maintenance/details/${item.maintenanceContractId}`;
            history.push(url);
            return;
        }
        if (item.insuranceContractId && item.vehicleId) {
            const url = `/vehicles/details/${item.vehicleId}/contract/details/insurance/details/${item.insuranceContractId}`;
            history.push(url);
            return;
        }

        if (event.ctrlKey) {
            window.open(`/reminders/details/${item.id}`, '_blank');
            return;
        }
        history.push(`/reminders/details/${item.id}`);
    };

    const filterItems = useMemo<FilterBadgeItem[]>(() => {
        const items: FilterBadgeItem[] = [];

        if (filters.vehicleId && filters.vehicleName) {
            items.push({
                key: 'vehicle',
                label: t('reminders.filters.vehicle') as string,
                value: filters.vehicleName,
                onRemove: () => onChangeFilters({ ...filters, vehicleId: undefined, vehicleName: undefined })
            });
        }

        if (filters.categoryId && filters.categoryName) {
            items.push({
                key: 'category',
                label: t('reminders.filters.category') as string,
                value: filters.categoryName,
                onRemove: () => onChangeFilters({ ...filters, categoryId: undefined, categoryName: undefined })
            });
        }

        if (filters.statuses && filters.statuses.length > 0) {
            items.push({
                key: 'statuses',
                label: t('reminders.filters.status') as string,
                value: filters.statuses.map(s => t(`reminders.notifications.status.${s}` as any) as string).join(', '),
                onRemove: () => onChangeFilters({ ...filters, statuses: undefined })
            });
        }

        if (filters.criteriaType) {
            items.push({
                key: 'criteriaType',
                label: t('reminders.list.criteria_type') as string,
                value: t(`reminders.notifications.criteria.${filters.criteriaType}` as any) as string,
                onRemove: () => onChangeFilters({ ...filters, criteriaType: undefined })
            });
        }

        if (filters.reminderDateFrom != null || filters.reminderDateTo != null) {
            const from = filters.reminderDateFrom != null ? moment(filters.reminderDateFrom).format(DATE_FORMAT_DEFAUT) : '...';
            const to = filters.reminderDateTo != null ? moment(filters.reminderDateTo).format(DATE_FORMAT_DEFAUT) : '...';
            items.push({
                key: 'reminderDate',
                label: t('reminders.filters.reminder_date_range') as string,
                value: `${from} - ${to}`,
                onRemove: () => onChangeFilters({ ...filters, reminderDateFrom: undefined, reminderDateTo: undefined })
            });
        }

        const alertRangeBadges: AlertRangeBadge[] = [
            {
                key: 'alertDate',
                label: t('reminders.filters.alert_date_range') as string,
                presetsPrefix: 'reminders.filters.alert_days_presets',
                preset: filters.alertDaysPreset,
                from: filters.alertDateFrom,
                to: filters.alertDateTo,
                unit: t('common.days') as string,
                onRemove: () => onChangeFilters({ ...filters, alertDateFrom: undefined, alertDateTo: undefined, alertDaysPreset: undefined })
            },
            {
                key: 'alertKm',
                label: t('reminders.filters.alert_km_range') as string,
                presetsPrefix: 'reminders.filters.alert_km_presets',
                preset: filters.alertKmPreset,
                from: filters.alertKmFrom,
                to: filters.alertKmTo,
                unit: 'km',
                onRemove: () => onChangeFilters({ ...filters, alertKmFrom: undefined, alertKmTo: undefined, alertKmPreset: undefined })
            },
            {
                key: 'alertHours',
                label: t('reminders.filters.alert_hours_range') as string,
                presetsPrefix: 'reminders.filters.alert_hours_presets',
                preset: filters.alertHoursPreset,
                from: filters.alertHourFrom,
                to: filters.alertHourTo,
                unit: t('common.hours_symbol') as string,
                onRemove: () => onChangeFilters({ ...filters, alertHourFrom: undefined, alertHourTo: undefined, alertHoursPreset: undefined })
            },
        ];

        alertRangeBadges
            .filter(b => b.from != null || b.to != null)
            .forEach(b => {
                const isPreset = b.preset && b.preset !== 'other';
                const value = isPreset
                    ? t(`${b.presetsPrefix}.${b.preset}` as any) as string
                    : `${b.from ?? 0} - ${b.to ?? '...'} ${b.unit}`;
                items.push({ key: b.key, label: b.label, value, onRemove: b.onRemove });
            });

        if (filters.segmentId && filters.segmentName) {
            items.push({
                key: 'segment',
                label: t('vehicle.type') as string,
                value: filters.segmentName,
                onRemove: () => onChangeFilters({ ...filters, segmentId: undefined, segmentName: undefined })
            });
        }

        if (filters.localIds?.length && filters.localNames?.length) {
            items.push({
                key: 'local',
                label: `${t('vehicle.local')} (${filters.localOperator === 'AND' ? t('common.filter_all' as any) : t('common.filter_any' as any)})` as string,
                value: filters.localNames.join(', '),
                onRemove: () => onChangeFilters({ ...filters, localIds: [], localNames: [] })
            });
        }

        return items;
    }, [filters, t]);

    const getHeaderTitle = () => {
        if (!type) return t('reminders.title');
        if (type === 'new') return t('reminders.new');
        if (type === 'details') return t('reminders.details');
        if (type === 'edit') return t('reminders.edit');
        return t('reminders.title');
    };

    const renderThresholdLabel = (key: ThresholdKey, text: string) => (
        <span className={styles.editableLabel}>
            {text}
            <FaPencilAlt className={styles.pencilIcon} onClick={e => { e.stopPropagation(); setEditingThreshold(key); }} />
        </span>
    );

    return (
        <ScreenTitle title={t('reminders.title')}>
            <CompactPageLayout
                title={getHeaderTitle() as string}
                onBack={type ? () => history.goBack() : undefined}
                contentAreaClassName={styles.contentArea}
                middleContent={!type ? <FilterBadgesOverflow items={filterItems} align="right" /> : undefined}
                headerButtons={
                    !type ? (
                        <>
                            <Popover
                                contentContainerClassName={styles.filtersPopoverContainer}
                                containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' }}
                                onClickOutside={() => updateTotalFilters(filters)}
                                content={setIsPopoverOpen => <ReminderFiltersScreen
                                    filters={filters}
                                    showHours={!!counters?.hasHoursAssets}
                                    onFilter={_filters => { setIsPopoverOpen(false); onChangeFilters(_filters) }}
                                    onChange={_filters => updateTotalFilters(_filters)}
                                />}
                            >
                                {(isPopoverOpen, setIsPopoverOpen) => (
                                    <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                                        {filtersTotal > 0 &&
                                            <div className={styles.counterList}> <div className={styles.counterNumber}>{filtersTotal}</div> </div>
                                        }
                                    </ScreenHeaderButton>
                                )}
                            </Popover>
                            <ExportListingButton exportRef={exportListingRef} />
                            {hasRemindersWritePolicy && <ScreenHeaderButton icon={AddIcon} onClick={() => navigateTo('new')} />}
                        </>
                    ) : undefined
                }
            >
                {/* COUNTERS */}
                {!type && !isFirstLoad.current && counters &&
                    <div className={compactPageLayoutStyles.countersSection}>
                        <div className={compactPageLayoutStyles.rowCounters}>
                            <InfoCard
                                label={t('reminders.counters.total') as string}
                                value={counters.totalCount}
                                icon={AlertIcon}
                                backgroundColor={colors.counterTeal1}
                            />
                            <InfoCard
                                label={t('reminders.counters.active') as string}
                                value={counters.activeCount}
                                icon={AlertIcon}
                                backgroundColor={colors.counterTeal2}
                            />
                            <InfoCard
                                label={renderThresholdLabel('days', t('reminders.counters.alert_in_days', { days: thresholds.days }) as string)}
                                value={counters.alertInDaysCount}
                                icon={AlertIcon}
                                backgroundColor={colors.counterBlue1}
                            />
                            <InfoCard
                                label={renderThresholdLabel('km', t('reminders.counters.alert_in_km', { km: thresholds.km.toLocaleString() }) as string)}
                                value={counters.alertInKmCount}
                                icon={AlertIcon}
                                backgroundColor={colors.counterBlue2}
                            />
                            {counters.hasHoursAssets && <InfoCard
                                label={renderThresholdLabel('hours', t('reminders.counters.alert_in_hours', { hours: thresholds.hours.toLocaleString() }) as string)}
                                value={counters.alertInHoursCount}
                                icon={AlertIcon}
                                backgroundColor={colors.counterBlue3}
                            />}
                        </div>
                    </div>
                }

                {/* EDIT THRESHOLD MODAL */}
                {editingThreshold && <ThresholdModal
                    fields={[{ key: editingThreshold, value: thresholds[editingThreshold], ...thresholdFields[editingThreshold] }]}
                    onApply={values => applyThreshold(editingThreshold, values[editingThreshold])}
                    onClose={() => setEditingThreshold(null)}
                />}

                {/* LIST */}
                {!type && !isFirstLoad.current &&
                    <ListRemindersScreen
                        editable={true}
                        criteria={criteria}
                        onClickItem={onRowClick}
                        exportRef={exportListingRef}
                    />
                }
                {/* DETAILS / EDIT / NEW */}
                {type && <Box className={styles.box}>
                    <ReminderScreen
                        onBackList={() => history.goBack()}
                    />
                </Box>}
            </CompactPageLayout>
        </ScreenTitle>
    );
};

export default RemindersScreen;
